/**
 * SearchHeader Component
 *
 * Header area with search summary and view toggle.
 */

import React from 'react';

interface SearchHeaderProps {
  location?: string;
  checkIn?: string;
  checkOut?: string;
  guests: number;
  resultCount: number;
  viewMode: 'grid' | 'map';
  onViewModeChange: (mode: 'grid' | 'map') => void;
  onOpenFilters: () => void;
  activeFilterCount: number;
}

export const SearchHeader: React.FC<SearchHeaderProps> = ({
  location,
  checkIn,
  checkOut,
  guests,
  resultCount,
  viewMode,
  onViewModeChange,
  onOpenFilters,
  activeFilterCount,
}) => {
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const searchSummary = [
    location || 'All locations',
    checkIn && checkOut ? `${formatDate(checkIn)} - ${formatDate(checkOut)}` : 'Any dates',
    `${guests} guest${guests !== 1 ? 's' : ''}`,
  ].join(' · ');

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">
          {resultCount} {resultCount === 1 ? 'stay' : 'stays'} {location ? `in ${location}` : 'in Lebanon'}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{searchSummary}</p>
      </div>

      <div className="flex items-center gap-3">
        {/* Mobile filter button */}
        <button
          type="button"
          onClick={onOpenFilters}
          className="lg:hidden flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:border-gray-400 transition-colors"
        >
          <FilterIcon className="w-4 h-4" />
          <span className="text-sm">Filters</span>
          {activeFilterCount > 0 && (
            <span className="w-5 h-5 bg-gray-900 text-white text-xs rounded-full flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>

        {/* View toggle */}
        <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => onViewModeChange('grid')}
            className={`p-2 ${
              viewMode === 'grid'
                ? 'bg-gray-900 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
            aria-label="Grid view"
          >
            <GridIcon className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => onViewModeChange('map')}
            className={`p-2 ${
              viewMode === 'map'
                ? 'bg-gray-900 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
            aria-label="Map view"
          >
            <MapIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Icons
const FilterIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
  </svg>
);

const GridIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
  </svg>
);

const MapIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
  </svg>
);

export default SearchHeader;
